import { useState } from 'react'
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react'

export interface LeagueNeighbor {
  rank: number
  name: string
  initials: string
  xp: number
  isYou?: boolean
}

export interface MyLeagueWidgetProps {
  leagueIcon: React.ReactNode
  leagueName: string
  nextLeagueName: string
  xpToNext: number
  neighbors: LeagueNeighbor[]
  inDemotionZone?: boolean
  xpToSafety?: number
  collapsible?: boolean
  defaultExpanded?: boolean
  onPress?: () => void
}

export function MyLeagueWidget({
  leagueIcon,
  leagueName,
  nextLeagueName,
  xpToNext,
  neighbors,
  inDemotionZone = false,
  xpToSafety = 0,
  collapsible = true,
  defaultExpanded = false,
  onPress,
}: MyLeagueWidgetProps) {
  const [expanded, setExpanded] = useState(!collapsible || defaultExpanded)

  const isOpen = !collapsible || expanded
  const youRow = neighbors.find((r) => r.isYou)

  const toggle = () => {
    if (collapsible) setExpanded((v) => !v)
  }

  return (
    <div
      style={{
        borderRadius: 20,
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.4)',
        borderLeft: '3px solid #C0A060',
        boxShadow: '0 8px 32px rgba(26,58,92,0.08)',
        overflow: 'hidden',
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      {/* Header */}
      <div
        onClick={toggle}
        style={{
          padding: '11px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}>{leagueIcon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1C2D41', display: 'block', lineHeight: 1.25 }}>{leagueName}</span>
          <span style={{ fontSize: 11, color: '#6B7B8D', display: 'block', marginTop: 1 }}>Моя лига</span>
        </div>

        {!isOpen && youRow ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(26,58,92,0.07)', border: '1px solid rgba(26,58,92,0.10)', borderRadius: 20, padding: '3px 9px', flexShrink: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#1C2D41' }}>#{youRow.rank}</span>
            <span style={{ fontSize: 11, color: '#A0AEC0', margin: '0 1px' }}>·</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6C5CE7' }}>{youRow.xp} XP</span>
          </div>
        ) : (
          <div style={{ display: 'inline-flex', alignItems: 'center', background: 'linear-gradient(135deg,rgba(108,92,231,0.10),rgba(162,155,254,0.14))', border: '1px solid rgba(108,92,231,0.18)', borderRadius: 20, padding: '3px 9px', flexShrink: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6C5CE7' }}>+{xpToNext} XP до {nextLeagueName}</span>
          </div>
        )}

        {collapsible ? (
          <ChevronDown size={16} color="#A0AEC0" style={{ flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)' }} />
        ) : (
          <ChevronRight size={16} color="#A0AEC0" style={{ flexShrink: 0 }} />
        )}
      </div>

      {/* Collapsible body */}
      <div style={{ maxHeight: isOpen ? 400 : 0, overflow: 'hidden', opacity: isOpen ? 1 : 0, transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.20s ease' }}>
        <div style={{ height: 1, background: 'rgba(26,58,92,0.06)', margin: '0 14px' }} />

        <div style={{ padding: '5px 0 2px' }}>
          {neighbors.map((row, i) => {
            const youIdx = neighbors.findIndex((r) => r.isYou)
            const isAbove = !row.isYou && i < youIdx
            const isYou = !!row.isYou

            return (
              <div key={row.rank} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px', background: isYou ? 'rgba(108,92,231,0.07)' : 'transparent' }}>
                {isYou && (
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'linear-gradient(180deg,#6C5CE7,#A29BFE)', borderRadius: '0 2px 2px 0' }} />
                )}
                <span style={{ fontSize: 12, fontWeight: isYou ? 700 : 500, color: isAbove ? '#C8D6DF' : isYou ? '#6C5CE7' : '#6B7B8D', width: 20, textAlign: 'center', flexShrink: 0 }}>{row.rank}</span>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: isYou ? 'linear-gradient(135deg,#6C5CE7,#A29BFE)' : isAbove ? 'rgba(200,214,223,0.40)' : 'rgba(26,58,92,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: isYou ? 'white' : isAbove ? '#C8D6DF' : '#6B7B8D', flexShrink: 0 }}>{row.initials}</div>
                <span style={{ flex: 1, fontSize: 13, fontWeight: isYou ? 700 : 400, color: isAbove ? '#C8D6DF' : isYou ? '#1C2D41' : '#4A5568', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.name}
                  {isYou && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 700, background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)', color: 'white', borderRadius: 5, padding: '1px 5px', verticalAlign: 'middle' }}>ВЫ</span>}
                </span>
                <span style={{ fontSize: 12, fontWeight: isYou ? 700 : 500, color: isAbove ? '#C8D6DF' : isYou ? '#6C5CE7' : '#6B7B8D', flexShrink: 0, minWidth: 42, textAlign: 'right' }}>{row.xp} XP</span>
              </div>
            )
          })}
        </div>

        {inDemotionZone && (
          <div style={{ background: 'rgba(255,149,0,0.09)', borderTop: '1px solid rgba(255,149,0,0.22)', padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#C96A00', display: 'flex', alignItems: 'center', gap: 5 }}>
              <TriangleAlert size={14} color="#C96A00" style={{ flexShrink: 0 }} />
              Угроза понижения
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#FF9500', whiteSpace: 'nowrap', flexShrink: 0 }}>+{xpToSafety} XP</span>
          </div>
        )}

        {onPress && (
          <div
            onClick={(e) => { e.stopPropagation(); onPress() }}
            style={{ padding: '8px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, borderTop: '1px solid rgba(26,58,92,0.05)', cursor: 'pointer' }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6C5CE7' }}>Открыть рейтинг</span>
            <ChevronRight size={13} color="#6C5CE7" />
          </div>
        )}
      </div>
    </div>
  )
}
